import type { TFunction } from 'i18next';

/**
 * Translates a navigation label or section heading.
 *
 * Nav entries are keyed by their ENGLISH text rather than by an explicit translation key,
 * because `MASTER_NAV` in `Layout.tsx` is also the source of truth for routes, permissions and
 * the top-bar crumb. Threading a second key through all of that invites the two drifting apart —
 * and the failure mode of drift is a raw `item.someKey` rendered in the sidebar.
 *
 * Keying off the English string instead means an entry added to `MASTER_NAV` without a
 * translation simply shows in English until someone adds one, which is the right degradation.
 */

/** 'Revenue & Reports' -> 'revenueAndReports', 'Lab AI Review' -> 'labAiReview'. */
export function navKeyFor(englishLabel: string): string {
  const tokens = englishLabel
    .replace(/&/g, ' And ')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!tokens.length) return '';
  return tokens
    .map((tok, i) =>
      i === 0
        ? tok.toLowerCase()
        : tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase(),
    )
    .join('');
}

export function navLabel(t: TFunction, englishLabel: string): string {
  return t(`nav:item.${navKeyFor(englishLabel)}`, { defaultValue: englishLabel });
}

export function navSectionLabel(t: TFunction, englishTitle: string): string {
  return t(`nav:section.${navKeyFor(englishTitle)}`, { defaultValue: englishTitle });
}
